/**
 * Google OAuth access-gate store (task 3127).
 *
 * Tracks the Stream operator's Google Workspace sign-in state. This store is a
 * thin mirror that drives the SignInGate render; the actual sign-in is done by
 * `staffSessionProvider`, which picks the desktop or browser path at load
 * (Task 4092 step 1).
 *
 * Desktop: the Rust layer owns the OAuth flow and Keychain storage.
 * Browser: Google Identity Services returns a short-lived ID token, and the
 * email is resolved via `GET /v1/auth/staff/me`.
 *
 * Status semantics:
 * - `unknown`         — initial; we have not yet asked the Rust layer.
 * - `checking`        — `check_google_auth` is in flight (Keychain hit +
 *                       silent refresh).
 * - `authenticated`   — the operator has a valid `@kodeon.ai` token. The
 *                       app may unlock.
 * - `unauthenticated` — no token, the token is invalid, or the account is
 *                       not part of the `kodeon.ai` Workspace. Render the
 *                       SignInGate.
 * - `signing_in`      — the browser is open; `start_google_sign_in` is
 *                       awaiting the callback. Render the SignInGate with
 *                       a "waiting" affordance.
 *
 * Note: the store itself is not persisted — we re-resolve on every launch via
 * `checkAuth()`. Desktop's source of truth is the Rust Keychain. In the browser
 * the ID token is mirrored to localStorage (see the subscription at the bottom
 * of this file) so a reload can restore it until it expires.
 */

import { create } from 'zustand'
import { api } from '@/lib/api'
import { IS_TAURI, fetchStaffProfile, staffSessionProvider } from '../lib/staff-session'
import { cancelRefreshForSignOut } from '../lib/token-refresh'
import { clearIdToken, saveIdToken } from '../lib/id-token-storage'

export type GoogleAuthStatus =
  | 'unknown'
  | 'checking'
  | 'authenticated'
  | 'unauthenticated'
  | 'signing_in'

interface GoogleAuthState {
  status: GoogleAuthStatus
  /** Operator email on success. May be empty after a fail-open network error. */
  email: string | null
  /**
   * Google ID token, browser only. The desktop path keeps its token in the Rust
   * layer and never exposes it to JS, so this stays null there.
   *
   * Short-lived (~1 hour) and NOT refreshed here — Task 4092 step 4 owns
   * refresh and retry-on-401. Treat it as valid only for the current boot.
   */
  idToken: string | null
  /** Last error from the sign-in path, if any. */
  error: string | null

  checkAuth: () => Promise<void>
  startSignIn: () => Promise<void>
  /**
   * Browser only: accept a credential from Google's rendered button and
   * resolve it to a staff identity. The desktop flow uses startSignIn, which
   * drives the Rust OAuth round-trip; the browser cannot, because One Tap's
   * prompt() is not click-drivable (see gis.ts renderSignInButton).
   */
  completeBrowserSignIn: (idToken: string) => Promise<void>
  signOut: () => Promise<void>
}

export const useGoogleAuthStore = create<GoogleAuthState>()((set) => ({
  status: 'unknown',
  email: null,
  idToken: null,
  error: null,

  checkAuth: async () => {
    set({ status: 'checking', error: null })
    try {
      const session = await staffSessionProvider.restore()
      if (session !== null) {
        // Desktop may return an empty email as a network-error fail-open: we
        // are already authenticated on this machine, so unlock the app.
        set({
          status: 'authenticated',
          email: session.email || null,
          idToken: session.idToken ?? null,
        })
      } else {
        set({ status: 'unauthenticated' })
      }
    } catch (err) {
      // Definitive auth failure (token revoked, account suspended, non-
      // @kodeon.ai account, no staff record). Desktop has already cleared the
      // stored token.
      set({ status: 'unauthenticated', error: toMessage(err) })
    }
  },

  startSignIn: async () => {
    set({ status: 'signing_in', error: null })
    try {
      const session = await staffSessionProvider.signIn()
      set({
        status: 'authenticated',
        email: session.email || null,
        idToken: session.idToken ?? null,
      })
    } catch (err) {
      set({ status: 'unauthenticated', error: toMessage(err) })
    }
  },

  completeBrowserSignIn: async (idToken: string) => {
    set({ status: 'signing_in', error: null })
    try {
      const profile = await fetchStaffProfile(idToken)
      set({ status: 'authenticated', email: profile.email, idToken })
    } catch (err) {
      set({ status: 'unauthenticated', idToken: null, error: toMessage(err) })
    }
  },

  signOut: async () => {
    await staffSessionProvider.signOut()

    // Tear down the API client too, not just this store (PR #109 B1).
    // Clearing the store alone renders the SignInGate while the client keeps
    // its credential: live SSE streams keep delivering to a "signed out" tab,
    // new requests still carry the token, and — worst — the refresher is still
    // installed, so the next 401 silently re-authenticates and flips the store
    // back to `authenticated`. Sign-out would undo itself.
    //
    // Desktop is untouched: it installs no refresher, and its credential is a
    // provisioned API key whose lifetime is not tied to this Google session.
    if (!IS_TAURI) {
      // Invalidate any refresh already awaiting GIS. Dropping the refresher
      // below only stops a NEW one from starting (PR #109 N-A).
      cancelRefreshForSignOut()
      api.setCredentialRefresher(null)
      api.abortAllStreams()
      api.clearCredential()
    }

    set({ status: 'unauthenticated', email: null, idToken: null, error: null })
  },
}))

// Browser: mirror the ID token to storage so a reload restores the session.
// Subscribing here rather than writing at each call site covers every path that
// changes the token — sign-in, the 401 refresh in token-refresh.ts, sign-out,
// and a failed profile lookup — including any added later.
if (!IS_TAURI) {
  useGoogleAuthStore.subscribe((state, prev) => {
    if (state.idToken === prev.idToken) return
    if (state.idToken) {
      saveIdToken(state.idToken)
    } else {
      clearIdToken()
    }
  })
}

/**
 * Render an error for the SignInGate.
 *
 * `staff_not_found` is the one case worth translating: the operator has a valid
 * kodeon.ai Google account but no `staff_members` record, and the fix is an
 * admin action rather than anything they can do at the gate.
 */
function toMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err)
  if (raw.includes('staff_not_found')) {
    return 'Your @kodeon.ai account has no Stream staff record yet. Ask an admin to create one.'
  }
  return raw
}
